import { useId } from "react";
import type * as React from "react";

import { useDialog } from "../interaction";
import { cn } from "../lib/cn";

export type DialogSize = "sm" | "md" | "takeover";

const dialogSize: Record<DialogSize, string> = {
  sm: "max-h-[85vh] w-[calc(100vw-2rem)] max-w-sm",
  md: "max-h-[85vh] w-[calc(100vw-2rem)] max-w-xl",
  takeover:
    "h-[calc(100vh-clamp(2rem,8vw,6rem))] w-[calc(100vw-clamp(2rem,8vw,6rem))] max-w-5xl",
};

const pad: Record<
  DialogSize,
  { header: string; body: string; footer: string }
> = {
  sm: { header: "px-5 pt-5 pb-3", body: "px-5 py-4", footer: "px-5 py-3" },
  md: { header: "px-6 pt-6 pb-3", body: "px-6 py-4", footer: "px-6 py-4" },
  takeover: {
    header: "px-6 pt-6 pb-4 sm:px-8",
    body: "px-6 py-5 sm:px-8",
    footer: "px-6 py-4 sm:px-8",
  },
};

export interface DialogProps extends Omit<
  React.DialogHTMLAttributes<HTMLDialogElement>,
  "open" | "title" | "children" | "onClose" | "onCancel"
> {
  open: boolean;
  onClose: () => void;
  /** When omitted, pass `aria-label`; the header is skipped. */
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: DialogSize;
  dismissable?: boolean;
  className?: string;
  ref?: React.Ref<HTMLDialogElement>;
}

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  size = "takeover",
  dismissable = true,
  className,
  ref,
  ...rest
}: DialogProps) {
  const titleId = useId();
  const { dialogRef, handleCancel, handleClose } = useDialog({
    open,
    onClose,
    dismissable,
  });

  return (
    <dialog
      {...rest}
      ref={(node) => {
        dialogRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      aria-labelledby={title !== undefined ? titleId : undefined}
      onCancel={handleCancel}
      onClose={handleClose}
      className={cn(
        // `open:flex`, never base `flex`, or it overrides `dialog:not([open]) { display: none }`.
        "m-auto flex-col overflow-visible bg-transparent p-0 whitespace-normal text-left font-body text-ink outline-none open:flex",
        "transition-[opacity,translate,overlay,display] transition-discrete duration-200 ease-paper",
        "translate-y-3 opacity-0 open:translate-y-0 open:opacity-100 starting:open:translate-y-3 starting:open:opacity-0",
        "backdrop:bg-nav/50 backdrop:transition-[opacity,display] backdrop:transition-discrete backdrop:duration-200",
        "backdrop:opacity-0 open:backdrop:opacity-100 starting:open:backdrop:opacity-0",
        "motion-reduce:transition-none motion-reduce:backdrop:transition-none",
        dialogSize[size],
        className,
      )}
    >
      {/* The only child, so useDialog's backdrop hit-test measures the sheet. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden break-words rounded-sheet border border-rule bg-card shadow-sheet">
        {title !== undefined && (
          <header className={cn("border-b border-rule", pad[size].header)}>
            <h2
              id={titleId}
              className="font-display text-lg font-medium tracking-tight text-ink"
            >
              {title}
            </h2>
          </header>
        )}
        <div
          className={cn(
            "min-h-0 flex-1 overflow-y-auto text-ink-body",
            pad[size].body,
          )}
        >
          {children}
        </div>
        {footer && (
          <div
            className={cn(
              "flex flex-wrap justify-end gap-3 border-t border-rule",
              pad[size].footer,
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
}
